import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { differenceInCalendarDays, format } from 'date-fns';
import { map, Observable } from 'rxjs';
import { SMARTBNB_API_V1 } from '../../interfaces/injection-tokens.interface';

interface ReservationApiItem {
  checkin: string; // ISO string
  checkout: string; // ISO string
  start_date?: string; // YYYY-MM-DD
  end_date?: string; // YYYY-MM-DD
  [key: string]: unknown;
}

export type ReservationWithNights = ReservationApiItem & { num_nights: number };

@Injectable({ providedIn: 'root' })
export class ReservationsService {
  private readonly http = inject(HttpClient);
  private readonly smartbnbApiV1 = inject(SMARTBNB_API_V1);

  getReservationsByDate(
    startDate: Date,
    endDate: Date,
    propertyIDs?: Array<string | number>,
    withTimezones = false,
    confirmedOnly = false,
    includeParentChildReservations = false
  ): Observable<ReservationWithNights[]> {
    const dateUrlParam = `${format(startDate, 'YYYY-MM-DD')}_${format(endDate, 'YYYY-MM-DD')}`;

    let url = `${this.smartbnbApiV1}/reservations/?starts_or_ends_between=${dateUrlParam}&timezones=${withTimezones}`;

    if (propertyIDs && propertyIDs.length > 0) {
      url += `&property_ids=${propertyIDs.join(',')}`;
    }

    if (confirmedOnly) {
      url += `&calendar_blockable=true`;
    }

    if (includeParentChildReservations) {
      url += `&include_family_reservations=true`;
    }

    return this.http.get<{ data: ReservationApiItem[] }>(url).pipe(
      map((response) => {
        const items = response.data ?? [];
        return items.map((event) => {
          const start = (event.start_date ?? event.checkin) as string;
          const end = (event.end_date ?? event.checkout) as string;
          const num_nights = differenceInCalendarDays(new Date(end), new Date(start));
          return { ...event, num_nights } as ReservationWithNights;
        });
      })
    );
  }
}
